import type { RepoDraft } from "@/types";

// #278 (BRIEF §2.2): die UI spricht FACHSPRACHE. Branch, Pull-Request, Merge, Tag und
// Commit-SHA tauchen in der Oberfläche NIE auf — sie sind die Git-Realität darunter.
//
// Pure Funktionen ohne DOM: so ist das Vokabular testbar, ohne eine Komponente zu rendern,
// und es gibt genau EINE Stelle, an der ein Zustand seinen Namen bekommt.

export type DraftState = RepoDraft["state"];

export function draftStateLabel(state: DraftState): string {
  switch (state) {
    case "draft":
      return "In Arbeit";
    case "submitted":
      return "Zur Freigabe eingereicht";
    default:
      return state;
  }
}

// Nutzt die BESTEHENDEN Badge-Varianten — kein neues Farb-Vokabular für diesen einen Fall.
export function draftStateVariant(state: DraftState): "outline" | "busy" {
  return state === "submitted" ? "busy" : "outline";
}

// Der Ref, den die UI führt: entweder der Live-Stand oder ein Entwurf.
export const LIVE_REF = "__live__";

export function isLive(ref: string): boolean {
  return ref === LIVE_REF;
}

/** Was an die API geht — für „live" nichts (der Server defaultet auf den Live-Branch). */
export function apiRef(ref: string): string | undefined {
  return isLive(ref) ? undefined : ref;
}

export function refLabel(ref: string, drafts: RepoDraft[]): string {
  if (isLive(ref)) return "Live (freigegeben)";
  return drafts.find((d) => d.branch === ref)?.title ?? ref;
}

/** Aus einem freien Titel einen gültigen Entwurfs-Namen machen (Server-Regel gespiegelt:
 *  `[a-z0-9][a-z0-9-]{0,60}`). Leeres Ergebnis = der Aufrufer muss nachfragen. */
export function slugify(input: string): string {
  const s = (input ?? "")
    .toLowerCase()
    // ⚠ Reihenfolge: die Umlaut-Ersetzung MUSS vor der Normalisierung laufen. `NFKD`
    // zerlegt „ä" in „a" + kombinierendes Trema — danach greift kein /ä/ mehr, und aus
    // „Grüße" würde „gru-sse" statt „gruesse".
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // übrige diakritische Zeichen (é, ç …) fallen weg
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 61)
    .replace(/-+$/g, ""); // der Zuschnitt darf keinen Bindestrich am Ende hinterlassen
  return /^[a-z0-9]/.test(s) ? s : "";
}

/** Warum ist Speichern gerade nicht möglich? null = es ist möglich. */
export function writeBlockedReason(
  lifecycle: "governed" | "direct" | undefined,
  ref: string,
): string | null {
  if (lifecycle === "direct") return null; // Agent-Workspace: direktes Schreiben ist erlaubt
  if (!isLive(ref)) return null;
  return "Der Live-Stand ist schreibgeschützt. Änderungen laufen über einen Entwurf, der freigegeben wird.";
}

/** Der Fehler-Code des Servers in einen Satz, den ein Admin versteht. */
export function lifecycleErrorMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case "LIVE_BRANCH_WRITE_FORBIDDEN":
      return "Auf den Live-Stand kann nicht direkt geschrieben werden — bitte einen Entwurf wählen.";
    case "DRAFT_ALREADY_EXISTS":
      return "Ein Entwurf mit diesem Namen existiert bereits.";
    case "INVALID_DRAFT_NAME":
      return "Ungültiger Entwurfs-Name (nur Kleinbuchstaben, Ziffern und Bindestriche).";
    case "DRAFT_NOT_FOUND":
      return "Dieser Entwurf existiert nicht (mehr).";
    case "ARTIFACT_SCHEMA_INVALID":
      return "Die Freigabe wurde abgelehnt: eine geänderte Datei ist ungültig.";
    case "PULL_NOT_MERGEABLE":
      return "Der Entwurf lässt sich nicht freigeben — bitte aktualisieren und erneut versuchen.";
    case "FOUR_EYES_REQUIRED":
      return "Vier-Augen-Prinzip: diesen Entwurf hat zuletzt dieselbe Person bearbeitet.";
    case "INVALID_APPROVAL_OVERRIDE":
      return "Für das Übergehen ist eine Begründung nötig.";
    case "VERSION_TAG_EXISTS":
      return "Diese Version existiert bereits.";
    case "INVALID_VERSION_TAG":
      return "Ungültiger Versions-Name (Muster: v1, v1.2.0).";
    case "TOO_MANY_CHANGED_FILES":
      return "Zu viele geänderte Dateien für eine Prüfung.";
    // #276/#277 (Review-Fund #8): Die Dialoge hatten je EIGENE Mappings mit teils anderen
    // Sätzen für dieselben Codes — sechs Tabellen, die driften. Alle Codes leben jetzt hier.
    case "REPO_ALREADY_EXISTS":
      return "Unter diesem Repository-Namen existiert bereits etwas — bitte einen anderen wählen.";
    case "MARKETPLACE_NAME_TAKEN":
      return "Dieser Marketplace-Name ist schon vergeben.";
    case "INVALID_REPO_NAME":
      return "Der Repository-Name ist ungültig (erlaubt: Buchstaben, Ziffern, . _ -).";
    case "INVALID_MARKETPLACE_CONFIG":
      return "Der Name muss kebab-case sein (Kleinbuchstaben, Ziffern, Bindestriche).";
    case "INVALID_PLUGIN_NAME":
      return "Der Plugin-Name ist ungültig (Kleinbuchstaben, Ziffern, . _ - ; kein @, kein Schrägstrich).";
    case "PLUGIN_NAME_TAKEN":
      return "Ein Plugin mit diesem Namen steht schon im Katalog.";
    case "MARKETPLACE_NOT_MANAGEABLE":
      return "Dieser Katalog liegt nicht auf unserem Gitea — dort können wir nichts eintragen.";
    case "UNKNOWN_SEED_SOURCE":
      return "Diese Quelle gibt es nicht (mehr).";
    case "INVALID_SCAFFOLD_TARGET":
      return "Dieses Repository kann nicht als Quelle dienen.";
    case "TOO_MANY_SCAFFOLDED_REPOS":
      return "Das Limit für selbst angelegte Repositories ist erreicht.";
    case "REPO_WRITE_CONFLICT":
      return "Zwischenzeitlich geändert — bitte neu laden und erneut speichern.";
    case "REPO_FILE_TOO_LARGE":
      return "Datei zu groß (Limit 10 MB).";
    case "INVALID_FILE_ENCODING":
      return "Der Dateiinhalt ist beschädigt (ungültige Kodierung) — bitte erneut hochladen.";
    case "INVALID_REPO_PATH":
      return "Mindestens ein Datei- oder Ordnername ist nicht zulässig.";
    case "ARCHIVE_DUPLICATE_PATH":
      return "Zwei Dateien haben denselben Namen (auch Groß-/Kleinschreibung zählt).";
    case "ARCHIVE_TOO_LARGE":
    case "ARCHIVE_TOO_MANY_FILES":
      return "Die Auswahl ist zu groß.";
    case "INVALID_ARCHIVE":
    case "UNSUPPORTED_ARCHIVE_ENTRY":
      return "Das Archiv ist nicht lesbar oder enthält nicht unterstützte Einträge.";
    case "APPROVAL_FORBIDDEN":
      return "Keine Berechtigung, dieses Artefakt freizugeben.";
    default:
      return fallback;
  }
}

/**
 * Bequemer Einstieg für Dialoge: zieht den Fehler-Code aus einer ApiError-Antwort und
 * übersetzt ihn über DIESELBE Tabelle wie die Lifecycle-Leiste. Kein Dialog baut mehr ein
 * eigenes Mapping — sechs driftende Tabellen waren der Review-Fund.
 */
export function repoErrorMessage(err: unknown, fallback: string): string {
  const code =
    typeof err === "object" && err !== null && "body" in err
      ? ((err as { body?: { code?: string } }).body?.code)
      : undefined;
  if (code) return lifecycleErrorMessage(code, fallback);
  return err instanceof Error ? err.message : fallback;
}

// #293 (UC-2): Feld-Diffs einer ausstehenden Plugin-Änderung als Fach-Satz — EINE
// Formatierung für Arbeitsliste UND Plugin-Detail-Banner. `source` ist nur ein Flag
// (der Wert ist Infrastruktur und bleibt serverseitig).
export function feldAenderungLabel(felder: Array<{ feld: string; alt: string | null; neu: string | null }>): string {
  const kurz = (v: string | null) => {
    if (v === null || v === "") return "—";
    return v.length > 40 ? `${v.slice(0, 40)}…` : v;
  };
  return felder
    .map((f) =>
      f.feld === "description"
        ? `Beschreibung: „${kurz(f.alt)}" → „${kurz(f.neu)}"`
        : f.feld === "version"
          ? `Version: ${kurz(f.alt)} → ${kurz(f.neu)}`
          : "Quelle geändert",
    )
    .join(" · ");
}
